// src/pages/RegisterPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertTriangle, Download, Share } from 'lucide-react';
import authApi from '../authApi';
import {
  parseAndStoreOnboardingCode,
  isInAppBrowser,
  isIOS,
  isStandalone
} from '../utils/onboardingUtils';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState('register');
  const [academyCode, setAcademyCode] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);

  const inAppWebview = isInAppBrowser();
  const isIOSDevice = isIOS();
  const alreadyInstalled = isStandalone();

  useEffect(() => {
    // 1. Check & store query code or localStorage code
    const storedCode = parseAndStoreOnboardingCode();
    if (storedCode) {
      setAcademyCode(storedCode);
    }

    // 2. If already logged in, redirect to dashboard
    if (localStorage.getItem('access_token')) {
      navigate('/dashboard', { replace: true });
    }

    // 3. Listen for PWA install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authApi.post('/api/auth/register/', formData);

      // Save tokens for both 201 and 202
      const accessToken = response.data.tokens?.access || response.data.access;
      const refreshToken = response.data.tokens?.refresh || response.data.refresh;
      if (accessToken) localStorage.setItem('access_token', accessToken);
      if (refreshToken) localStorage.setItem('refresh_token', refreshToken);

      if (response.status === 202 && response.data.requires_profile_selection) {
        // Sibling match — navigate to profile selection
        navigate('/select-profile', {
          state: { matched_players: response.data.matched_players },
        });
      } else {
        // Standard 201 creation
        setOnboardingStep('academy_code');
      }
    } catch (err) {
      if (err.response && err.response.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          setError(data);
        } else if (data.detail) {
          setError(data.detail);
        } else {
          const firstKey = Object.keys(data)[0];
          const firstError = Array.isArray(data[firstKey]) ? data[firstKey][0] : data[firstKey];
          setError(`${firstKey}: ${firstError}`);
        }
      } else {
        setError('Registration failed. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLinkAccount = async (e) => {
    e.preventDefault();
    setLinking(true);
    setLinkError('');
    try {
      const { linkAccount } = await import('../api');
      await linkAccount(academyCode);
      // Clean stored code after successful link
      localStorage.removeItem('solosync_link_code');
      navigate('/dashboard');
    } catch (err) {
      setLinkError(err.response?.data?.error || 'Invalid or expired code. Please try again.');
      setLinking(false);
    }
  };

  const handleSkipLink = () => {
    localStorage.removeItem('solosync_link_code');
    navigate('/dashboard');
  };

  const handleTriggerPWAInstall = async () => {
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      const choiceResult = await deferredInstallPrompt.userChoice;
      if (choiceResult.outcome === 'accepted') {
        setDeferredInstallPrompt(null);
      }
    }
  };

  if (onboardingStep === 'academy_code') {
    return (
      <div
        className="flex items-center justify-center min-h-screen bg-cover bg-center bg-no-repeat relative"
        style={{ backgroundImage: "url('/images/login-bg.png')" }}
      >
        <div className="absolute inset-0 bg-gray-900/60" />
        <div className="relative w-full max-w-md p-6 sm:p-8 space-y-6 bg-gray-900/10 backdrop-blur-md shadow-2xl rounded-2xl border border-white/20 z-10">
          
          {/* In-App Browser Warning Banner */}
          {inAppWebview && (
            <div className="bg-amber-500/20 border border-amber-500/40 rounded-xl p-3 text-amber-200 text-xs flex items-start gap-2.5 shadow-sm">
              <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400 mt-0.5" />
              <span>For the best experience and to install the app, open this link in Safari or Chrome.</span>
            </div>
          )}

          <div className="text-center">
            <h2 className="text-2xl font-bold text-white">Do you have an Academy Access Code?</h2>
            <p className="text-gray-400 text-sm mt-2">If you were given a code by your coach, enter it here.</p>
          </div>

          <form onSubmit={handleLinkAccount} className="space-y-5">
            <div>
              <input
                type="text"
                value={academyCode}
                onChange={(e) => setAcademyCode(e.target.value.toUpperCase())}
                placeholder="ENTER ACCESS CODE"
                className="w-full px-3 py-2 mt-1 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500 text-white bg-gray-800 text-center tracking-widest font-bold"
                required
              />
            </div>
            {linkError && <p className="text-sm text-red-400 font-medium text-center">{linkError}</p>}
            <button
              type="submit"
              disabled={linking}
              className="w-full px-4 py-2.5 font-semibold text-gray-900 bg-gradient-to-r from-teal-400 to-teal-500 rounded-md hover:from-teal-300 hover:to-teal-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {linking ? 'Linking...' : 'Link Account'}
            </button>
            <button
              type="button"
              onClick={handleSkipLink}
              disabled={linking}
              className="w-full px-4 py-2.5 font-semibold text-white bg-gray-700/50 rounded-md hover:bg-gray-600/50 focus:outline-none transition-all disabled:opacity-50"
            >
              Skip for now
            </button>
          </form>

          {/* PWA Installation Guidance */}
          {!alreadyInstalled && (
            <div className="bg-gray-800/80 border border-gray-700/80 rounded-xl p-4 space-y-2 text-xs text-gray-300 text-left mt-4 shadow-md">
              <div className="font-bold text-teal-300 flex items-center gap-1.5 text-sm">
                <Download className="w-4 h-4" /> Install SoloSync PWA App
              </div>
              {isIOSDevice ? (
                <div className="space-y-1.5 text-gray-300 mt-1">
                  <p className="flex items-center gap-2">
                    <span className="bg-teal-500/20 text-teal-300 font-bold px-2 py-0.5 rounded text-[11px]">1</span>
                    Tap the <span className="font-semibold text-white">Share</span> button ( <Share className="w-3.5 h-3.5 inline text-blue-400" /> ) in Safari.
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="bg-teal-500/20 text-teal-300 font-bold px-2 py-0.5 rounded text-[11px]">2</span>
                    Scroll down and select <span className="font-semibold text-white">"Add to Home Screen"</span>.
                  </p>
                </div>
              ) : deferredInstallPrompt ? (
                <button
                  type="button"
                  onClick={handleTriggerPWAInstall}
                  className="w-full mt-2 py-2 bg-gradient-to-r from-teal-400 to-teal-500 hover:from-teal-300 hover:to-teal-400 text-gray-900 font-bold rounded-lg transition-colors text-xs shadow-sm flex items-center justify-center gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" /> Install App to Home Screen
                </button>
              ) : (
                <p className="text-gray-400 text-[11px]">To install, open your browser menu and choose "Add to Home Screen" or "Install App".</p>
              )}
            </div>
          )}

        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-center min-h-screen bg-cover bg-center bg-no-repeat relative"
      style={{ backgroundImage: "url('/images/login-bg.png')" }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gray-900/60" />

      {/* Form Container */}
      <div className="relative w-full max-w-md p-6 sm:p-8 space-y-6 bg-gray-900/10 backdrop-blur-md shadow-2xl rounded-2xl border border-white/20 z-10">
        
        {/* In-App Browser Warning Banner */}
        {inAppWebview && (
          <div className="bg-amber-500/20 border border-amber-500/40 rounded-xl p-3 text-amber-200 text-xs flex items-start gap-2.5 shadow-sm">
            <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400 mt-0.5" />
            <span>For the best experience and to install the app, open this link in Safari or Chrome.</span>
          </div>
        )}

        <div className="text-center">
          <h2 className="text-2xl font-bold text-white">Create Your Account</h2>
          <p className="text-gray-400 text-sm mt-2">Join SoloSync and start training smarter.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="first_name" className="text-sm font-medium text-gray-100">
                First Name
              </label>
              <input
                id="first_name"
                name="first_name"
                type="text"
                value={formData.first_name}
                onChange={handleChange}
                className="w-full px-3 py-2 mt-1 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500 text-white bg-gray-800"
                required
              />
            </div>
            <div>
              <label htmlFor="last_name" className="text-sm font-medium text-gray-100">
                Last Name
              </label>
              <input
                id="last_name"
                name="last_name"
                type="text"
                value={formData.last_name}
                onChange={handleChange}
                className="w-full px-3 py-2 mt-1 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500 text-white bg-gray-800"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="text-sm font-medium text-gray-100">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 mt-1 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500 text-white bg-gray-800"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="text-sm font-medium text-gray-100">
              Password
            </label>
            <div className="relative mt-1">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-teal-500 focus:border-teal-500 text-white bg-gray-800 pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
              >
                {showPassword ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-red-400 font-medium">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2.5 font-semibold text-gray-900 bg-gradient-to-r from-teal-400 to-teal-500 rounded-md hover:from-teal-300 hover:to-teal-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-400">
          Already have an account?{' '}
          <Link to="/login" className="text-teal-400 hover:text-teal-300 font-medium transition-colors">
            Log In
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
